import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { isSupabaseProjectRef } from '@brunel/shared';
import type { Guard } from '../App';
import type { NodePresence } from '../lib/data';
import { errorMessage } from '../lib/mutations';
import {
  buildSupabaseCliView,
  enrollCommand,
  parseSupabaseCliStatus,
  type SupabaseCliStatus,
} from '../lib/supabase-cli-view';

export interface SupabaseCliCardProps {
  readonly client: SupabaseClient;
  readonly guard: Guard;
  readonly orgId: string;
  readonly nodes: readonly NodePresence[];
  /** Tests render a known status without a network read. */
  readonly initialStatus?: SupabaseCliStatus | null;
}

/**
 * Supabase Production CLI (#520). Status and owner controls only; there is no
 * password field anywhere in the browser — enrollment happens in the OS prompt
 * on the designated computer.
 */
export function SupabaseCliCard({ client, guard, orgId, nodes, initialStatus = null }: SupabaseCliCardProps): ReactElement {
  const [status, setStatus] = useState<SupabaseCliStatus | null>(initialStatus);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [nodeId, setNodeId] = useState(nodes[0]?.node_id ?? '');
  const [projectRef, setProjectRef] = useState('');

  const load = useCallback(async () => {
    const { data, error: transportError } = await client.rpc('get_supabase_cli_status', { request: { orgId } });
    const result = data as { ok?: boolean; value?: unknown } | null;
    if (transportError || !result?.ok) return;
    setStatus(parseSupabaseCliStatus(result.value));
  }, [client, orgId]);

  useEffect(() => {
    if (initialStatus === null) void load();
  }, [initialStatus, load]);

  const view = useMemo(() => buildSupabaseCliView(status), [status]);

  async function manage(request: Record<string, string>, done: string): Promise<void> {
    setError(null);
    setOk(null);
    await guard('manage_supabase_cli_credential', async () => {
      setBusy(true);
      try {
        const { data, error: transportError } = await client.rpc('manage_supabase_cli_credential', {
          request: { orgId, ...request },
        });
        const result = data as { ok?: boolean; error?: { code?: string }; value?: { alreadyWaiting?: boolean } } | null;
        if (transportError || !result?.ok) {
          setError(result?.error?.code === 'conflict'
            ? 'Revoke the current credential before designating a different computer or project.'
            : errorMessage(result?.error?.code ?? 'internal'));
          return;
        }
        setOk(result.value?.alreadyWaiting ? 'Already waiting on the designated computer.' : done);
        await load();
      } finally {
        setBusy(false);
      }
    });
  }

  return (
    <section className="connector-center-group" aria-label="Supabase Production CLI">
      <header>
        <h3>Supabase Production CLI</h3>
        <small className="muted">
          Connector first. The CLI runs only for exact-version migration pushes, through the node Secret Broker on one designated computer.
        </small>
      </header>
      <article className="connector-center-card">
        <dl className="connector-center-facts">
          <div><dt>Connector</dt><dd>{view.connectorLabel}</dd></div>
          <div><dt>CLI credential</dt><dd>{view.credentialLabel}</dd></div>
          <div><dt>Project</dt><dd>{view.projectLabel}</dd></div>
          <div><dt>Last verified</dt><dd>{view.lastVerifiedLabel ?? 'Never'}</dd></div>
        </dl>
        {view.ownerAction && <p className="connector-center-notice">{view.ownerAction}</p>}
        {error && <p role="alert">{error}</p>}
        {ok && <p role="status" className="muted">{ok}</p>}
        <div className="row wrap connector-center-actions">
          {view.canTest && (
            <button type="button" disabled={busy} onClick={() => { void manage({ action: 'test' }, 'Credential test requested on the designated computer.'); }}>
              Test credential
            </button>
          )}
          {view.canTest && status?.credential && (
            <details>
              <summary>Re-enroll</summary>
              <small>
                On the designated computer, in a local Terminal: <code>{enrollCommand(status.credential.projectRef)}</code>.
                macOS asks for the password; Brunel never sees it.
              </small>
            </details>
          )}
          {view.canRevoke && (
            <button type="button" disabled={busy} onClick={() => { void manage({ action: 'revoke' }, 'Revoked. CLI operations fail closed until the owner designates and enrolls again.'); }}>
              Revoke
            </button>
          )}
          {view.canDesignate && (
            <form
              className="row wrap"
              onSubmit={(event) => {
                event.preventDefault();
                if (!isSupabaseProjectRef(projectRef)) {
                  setError('Enter the 20-letter Supabase project ref.');
                  return;
                }
                void manage({ action: 'designate', nodeId, projectRef }, 'Designated. Enroll the password once on that computer.');
              }}
            >
              <select aria-label="Deployment computer" value={nodeId} onChange={(event) => setNodeId(event.target.value)}>
                {nodes.map((node) => <option key={node.node_id} value={node.node_id}>{node.name}</option>)}
              </select>
              <input
                aria-label="Project ref"
                placeholder="project ref"
                value={projectRef}
                onChange={(event) => setProjectRef(event.target.value.trim())}
              />
              <button type="submit" disabled={busy || nodeId === ''}>Designate computer</button>
            </form>
          )}
        </div>
      </article>
    </section>
  );
}
